/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState } from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import { useTheme } from '../../../../context/themeContext';
import ThemeToggle from '../../../LandingPage/Components/ThemeToggle/ThemeToggle';

// Coachify Marketplace (this app) <-> Coachify Maps cross-link — same as
// TopbarDesktop's, mobile menu just needs its own copy since it's a
// separate component tree. ?dashboard=1 deep-links a signed-in coach
// straight to their Maps dashboard (see TopbarDesktop.js's copy of this
// comment for the full rationale).
const COACHIFY_MAPS_URL = 'https://discovery.coachifyusa.com/?dashboard=1';

// The 30-second "just put me on the map" form -- see TopbarDesktop.js for
// why this sits next to "Become a Coach" rather than replacing it.
const COACHIFY_MAPS_JOIN_URL = 'https://discovery.coachifyusa.com/join';

// Identity-unification Phase 3: one-click handoff into the matching
// Discovery/Maps dashboard, reusing the exact same Supabase account this
// person is already linked to (see server/api/auth/discoveryHandoff.js) --
// not a plain hyperlink like COACHIFY_MAPS_URL above, since it needs a
// live session to mint the handoff token against.
async function openDiscoveryDashboard() {
  try {
    const res = await fetch('/api/coachify-sso/discovery-handoff?target=%2F%3Fdashboard%3D1', {
      credentials: 'include',
    });
    const data = await res.json();
    if (data?.redirectUrl) {
      window.location.href = data.redirectUrl;
    }
  } catch (err) {
    // Best-effort -- the plain Coachify Maps link above still works as a fallback.
    console.error('Failed to open Discovery dashboard', err); // eslint-disable-line no-console
  }
}

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    onToggleBecomeCoachModal,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  // isAuthenticated only ever reflects the Sharetribe SDK's own cookie,
  // which native sessions never carry -- gating on it alone showed every
  // logged-in mobile user the logged-out menu (Sign up / Log in links)
  // with no way to reach their dashboard, inbox, or logout button.
  // currentUser is populated identically for both auth paths (see
  // Routes.js's canShowComponent for the same fix applied to route auth).
  if (!isAuthenticated && !currentUser) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <button
            type="button"
            className={css.navigationLink}
            onClick={() => onToggleBecomeCoachModal(true)}
          >
            Become a Coach
          </button>

          <ExternalLink href={COACHIFY_MAPS_JOIN_URL} className={css.navigationLink} target="_self">
            <span className={css.menuItemBorder} />
            Get on the map — free
          </ExternalLink>

          <div className={css.mapsLinkRow}>
            <ExternalLink href={COACHIFY_MAPS_URL} className={css.mapsLink} target="_self">
              <span className={css.menuItemBorder} />
              Coachify Maps
            </ExternalLink>
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
          </div>

          <div className={css.customLinksWrapper}>{extraLinks}</div>

          <div className={css.spacer} />
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };
  const inboxTab = currentUserHasListings ? 'sales' : 'orders';

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          {!user?.attributes?.profile?.publicData?.coach && (
            <button
              type="button"
              className={css.navigationLink}
              onClick={() => onToggleBecomeCoachModal(true)}
            >
              Become a Coach
            </button>
          )}
          <div className={css.mapsLinkRow}>
            <ExternalLink href={COACHIFY_MAPS_URL} className={css.mapsLink} target="_self">
              <span className={css.menuItemBorder} />
              Coachify Maps
            </ExternalLink>
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
          </div>
          <button
            type="button"
            className={classNames(css.navigationLink, css.dashboardToggle)}
            onClick={() => setIsDashboardOpen(v => !v)}
          >
            <span className={css.dashboardToggleRow}>
              <span>Dashboard</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={classNames(css.dashboardToggleIcon, {
                  [css.dashboardToggleIconOpen]: isDashboardOpen,
                })}
              >
                <path
                  d="M1 1.5L6 6.5L11 1.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
          {isDashboardOpen ? (
            <>
              {user?.attributes?.profile?.publicData?.coach && (
                <NamedLink
                  className={classNames(
                    css.navigationLink,
                    css.dashboardSubLink,
                    currentPageClass('ProviderPage')
                  )}
                  name="ProviderPage"
                  onClick={() => setIsDashboardOpen(false)}
                >
                  Provider
                </NamedLink>
              )}
              <NamedLink
                className={classNames(
                  css.navigationLink,
                  css.dashboardSubLink,
                  currentPageClass('CustomerPage')
                )}
                name="CustomerPage"
                onClick={() => setIsDashboardOpen(false)}
              >
                Customer
              </NamedLink>
              <button
                type="button"
                className={classNames(css.navigationLink, css.dashboardSubLink)}
                onClick={() => {
                  setIsDashboardOpen(false);
                  openDiscoveryDashboard();
                }}
              >
                {user?.attributes?.profile?.publicData?.coach
                  ? 'Coaches Dashboard (Maps)'
                  : 'Customer Dashboard (Maps)'}
              </button>
            </>
          ) : null}
          {/* <NamedLink
            className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink> */}
          {/* <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
          </NamedLink> */}
          {/* <a
            className={classNames(css.navigationLink, currentPageClass('FavouritesPage'))}
            name="FavouritesPage"
            href="/favourites"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.Favourites" />
          </a> */}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
          {/* <NamedLink
            className={classNames(css.navigationLink, currentPageClass('SendNotificationEmail'))}
            name="SendNotificationEmail"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage
              id="TopbarDesktop.sendNotificationEmail"
              defaultMessage={'Send Notification Email'}
            />
          </NamedLink> */}

          {/* <NamedLink
            className={classNames(css.menuLink, currentPageClass('CalenderPage'))}
            name="CalenderPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.calenderPage" defaultMessage={'Calender page'} />
          </NamedLink> */}
        </div>
        <div className={css.customLinksWrapper}>{extraLinks}</div>
        <div className={css.spacer} />
      </div>
      {/* <div className={css.footer}>
        <NamedLink className={css.createNewListingLink} name="NewListingPage">
          <FormattedMessage id="TopbarMobileMenu.newListingLink" />
        </NamedLink>
      </div> */}
    </div>
  );
};

export default TopbarMobileMenu;
